import { BrowserRouter } from 'react-router-dom';

import Home from './components/Home/Home';
import ThemeProvider from './components/ThemeProvider/ThemeProvider';
import createOktaInstance from './config/okta';
import { restoreOriginalUri } from './config/helpers';
import { BypassableSecurity } from './components/common/BypassableRoutes';
import * as Styles from './components/styles';
import { AnalyticsProvider } from './v2/third-party-integration/amplitude';
import getUserIdFromOkta from './util/getUserIdFromOkta';
import { useScriptInjector } from './hooks/useScriptInjector';
import { MessageProvider } from './components/MessageProvider/MessageProvider';
import { AppStateProvider } from './contexts/AppStateContext';
import { useUserProfile, useIAMUserProfile } from './hooks/queries';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FFContextProvider } from '@harnessio/ff-react-client-sdk'
import {REACT_APP_AHA_APPLICATION_ID, REACT_APP_HARNESS} from './constants';
import './i18n';

const isScriptDefer = false;
const isAsync = true;

const amplitudeKey = process.env.REACT_APP_AMPLITUDE_API_KEY;
const oktaAuth = createOktaInstance();

function App() {
  const userId = getUserIdFromOkta();
  const { data, status: userProfileStatus } = useUserProfile();
  const { data: iamUserProfile, isLoading: iamUserProfileLoading } = useIAMUserProfile();
  const { i18n } = useTranslation();
  window.WalkMeLoginId =  data?.userProfile?.email;

  const userLocale = data?.locale || 'en_US';

  const enterpriseIdFromOKTA = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')
      .accessToken
      ?.claims
      ?.enterpriseId || "";

  // Prefer the IAM profile; fall back to the OKTA claim while it loads or if the
  // request fails (useIAMUserProfile has retry disabled).
  const enterpriseId = iamUserProfile?.fullUser?.enterprise?.id || enterpriseIdFromOKTA;

  const email = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')
      .accessToken
      ?.claims
      ?.cdksid || "";

  const storeId = data?.userProfile?.defaultStore || "";

  const isUserProfileSettled = userProfileStatus === 'success' || userProfileStatus === 'error';
  const ffTargetReady = !!email && isUserProfileSettled && !iamUserProfileLoading;

  const ffTargetId = email.replace(/[^a-zA-Z0-9@.]/g, '') || 'UNIFY';
  const ffTarget = {
    identifier: ffTargetId,
    name: ffTargetId,
    attributes: { enterpriseId: enterpriseId, storeId: storeId, email: ffTargetId }
  };
  const ffTargetKey = JSON.stringify(ffTarget);

  const ahaScript = useScriptInjector(
    `/thirdParty/ahaideas.js`,
    isAsync,
    isScriptDefer
  );

  //const localeAbbr = userLocale === 'en_CA' ? 'ca' : userLocale.substring(0,2);

  React.useEffect(()=>{
    i18n.changeLanguage(userLocale);
  },[i18n, userLocale]);

  React.useEffect(()=>{
    if(ahaScript === 'ready') {
     const aha = window.aha;
     data?.userProfile && aha('initialize', {
          account: 'cdk',
          applicationId: REACT_APP_AHA_APPLICATION_ID,
          user: {
            id: data?.userProfile?.email,
            name: `${data?.userProfile?.firstName} ${data?.userProfile?.lastName}`,
            email: data?.userProfile?.email,
          },
      });
    }

  },[data, ahaScript]);

  useScriptInjector(
    '/thirdParty/qualtrics.feedback.min.js',
    isAsync,
    isScriptDefer
  );

  useScriptInjector(
    `/thirdParty/newRelic.${process.env.REACT_APP_ENV}.min.js`,
    isAsync,
    isScriptDefer
  );

  return (
    <Styles.App>
      <AppStateProvider>
        <BrowserRouter>
          <AnalyticsProvider apiKey={amplitudeKey} userId={userId} sessionReplay={true}>
            <BypassableSecurity
              oktaAuth={oktaAuth}
              restoreOriginalUri={restoreOriginalUri}
            >
              <ThemeProvider>
                    <MessageProvider>
                    <FFContextProvider
                      async={true}
                      apiKey={REACT_APP_HARNESS}
                      target={ffTarget}
                      options={{
                        streamEnabled: true,
                      }}
                      >
                      <Home ffTargetReady={ffTargetReady} ffTargetKey={ffTargetKey} />
                      </FFContextProvider>
                    </MessageProvider>
                </ThemeProvider>
            </BypassableSecurity>
          </AnalyticsProvider>
        </BrowserRouter>
      </AppStateProvider>
     
    </Styles.App>
  );
}

export default App;
